import type { CircleHitObject, CirclesChart } from '@shared/circlesChart';
import type { CirclesEngineState } from '@shared/engine/circles/state';
import { SLIDER_FOLLOW_RADIUS_MULTIPLIER } from '@shared/gameTypes';
import { buildSliderPath, pointAtDistance, type SliderPath } from '@shared/osuCurves';
import { sliderArcLengthAt, sliderTickDistances } from '@shared/osuSliderTiming';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import {
  SLIDER_BALL_RADIUS_MUL,
  SLIDER_BODY_COMPOSITE_ALPHA,
  SLIDER_BODY_WIDTH_MUL,
  SLIDER_FADE_OUT_BEATS,
  SLIDER_TICK_ALPHA,
  SLIDER_TICK_MIN_RADIUS_PX,
  SLIDER_TICK_POP_DISTANCE_MUL,
  SLIDER_TICK_POP_SWELL,
  SLIDER_TICK_RADIUS_MUL,
  SLIDER_TICK_RESPAWN_BEATS,
  SLIDER_TICK_SPACING_MUL,
} from './circlesRenderConstants';
import { comboColour, hexToRgba } from './circlesRenderColour';
import { SliderBodyRenderer } from './circlesSliderBody';
import type { HitObjectRenderer } from './circlesDrawHitObject';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Re-export so consumers can read the slider fade-out grace without
 * importing the constants module directly — keeps `circlesRenderer.ts`
 * dependent on one slider module instead of two.
 */
export { SLIDER_FADE_OUT_BEATS };

/**
 * Owns slider-specific render state: the lazy path tessellation cache
 * and an embedded {@link SliderBodyRenderer} for the offscreen body
 * pass. Both are render-only (the engine has its own path cache for
 * tracking math) and heavy to recompute per frame.
 */
export class SliderRenderer {
  /**
   * Lazy cache of slider geometric paths, keyed by hit-object id.
   * `buildSliderPath` tessellates each curve into a few hundred
   * points + cumulative arc-lengths; doing it once at first draw
   * and reusing across frames keeps the per-frame slider draw
   * cheap. Mirrors the engine's `CirclesEngineConfig.sliderPaths`
   * — kept independent here so the renderer doesn't have to plumb
   * the engine config through every draw call.
   */
  private sliderPathCache = new Map<string, SliderPath>();
  private bodyRenderer: SliderBodyRenderer;
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
    this.bodyRenderer = new SliderBodyRenderer(reducedMotion, quality);
  }

  /**
   * Lazily build + cache a slider's geometric path. `buildSliderPath`
   * is a few hundred sample-point + cumulative-length tessellations,
   * too expensive to redo per frame for every visible slider — but
   * cheap to cache once at first draw since slider data never
   * changes mid-run. The cache outlives the slider's visible window
   * (a chart with a 5-second slider re-uses its path the next loop).
   *
   * Mirrors {@link CirclesEngineConfig.sliderPaths} on the engine side
   * — both must use the same tessellation so the position the
   * renderer draws the ball at matches the position the engine's
   * tracking check uses.
   */
  private getSliderPath(obj: CircleHitObject): SliderPath | null {
    if (obj.kind !== 'slider' || !obj.slider) {
      return null;
    }
    const cached = this.sliderPathCache.get(obj.id);
    if (cached) {
      return cached;
    }
    // Prepend the head: chart `controlPoints` are stored post-head
    // (matches the .osu wire format). `buildSliderPath` expects the
    // head to be index 0; adding it here keeps the renderer's
    // tessellation byte-for-byte identical to the engine's, which
    // is required so the ball the player sees matches the position
    // the engine's follow-circle test runs against.
    const path = buildSliderPath(obj.slider.curveType, [
      { x: obj.x, y: obj.y },
      ...obj.slider.controlPoints,
    ]);
    this.sliderPathCache.set(obj.id, path);
    return path;
  }

  /**
   * Draw a slider hit object. Lifecycle phases:
   *
   *   - **Approach** (`beat < headBeat`): full curve body fades in
   *     alongside the head circle's approach ring. The body stays
   *     muted (combo-colour low-alpha fill) so it reads as "guide,
   *     not active". Reverse arrows on repeats are pre-shown so the
   *     player sees them coming.
   *
   *   - **Active** (`beat >= headBeat && beat <= endBeat`): the body
   *     stays drawn; a slider ball traces the curve at its
   *     beat-derived position; a follow circle (sized to the engine's
   *     forgiving 3.0× tracking radius) ringes the ball; reverse
   *     arrows light up at the upcoming repeat edge.
   *
   *   - **Fade-out** (`beat > endBeat`): everything fades over
   *     {@link SLIDER_FADE_OUT_BEATS}, then is culled.
   *
   * Tracking visualisation: the follow circle changes alpha based on
   * the engine's `trackingNow` state — bright when tracking, dim
   * when lost. The ball stays at full brightness regardless so the
   * player can always tell where it is, even when they've dropped
   * tracking.
   *
   * Delegates the head circle visuals to the shared
   * {@link HitObjectRenderer.drawCircle} so the head reads identically
   * to a standalone circle (combo number, approach ring, perfect-window
   * halo, hit-window glow).
   */
  drawSlider(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    obj: CircleHitObject,
    t: number,
    field: PlayfieldRect,
    beat: number,
    dist: number,
    perfectWindowBeats: number,
    goodWindowBeats: number,
    kiaiScaleBoost: number,
    state: CirclesEngineState,
    dpr: number,
    hitObjectRenderer: HitObjectRenderer,
    quality: RenderQuality,
  ): void {
    const slider = obj.slider;
    if (!slider) {
      return;
    }
    const path = this.getSliderPath(obj);
    if (!path) {
      return;
    }
    const colour = comboColour(chart, obj.comboIndex);
    const innerR = chart.circleRadius * field.scale * (1 + kiaiScaleBoost);

    // Fade envelope — same general shape as a circle's fade-in, with
    // an additional fade-out tail past `endBeat`. Pre-`headBeat` we
    // mirror the circle's 0.67-of-approach fade. Post-`endBeat` we
    // ramp linearly to 0 over `SLIDER_FADE_OUT_BEATS`.
    let alpha = 1;
    const FADE_IN_FRACTION = 0.67;
    if (t > 1 - FADE_IN_FRACTION && t <= 1) {
      alpha = (1 - t) / FADE_IN_FRACTION;
    } else if (t > 1) {
      alpha = 0;
    }
    if (beat > slider.endBeat) {
      const fadeOutT = Math.max(
        0,
        Math.min(1, (beat - slider.endBeat) / SLIDER_FADE_OUT_BEATS),
      );
      alpha = Math.min(alpha, 1 - fadeOutT);
    }
    if (alpha <= 0) {
      return;
    }

    // === Body ===
    // Body width = full diameter (head/tail caps match). The body is
    // rendered on an offscreen canvas at full opacity to collapse
    // self-overlapping segments into a uniform-width track. We then
    // composite back at SLIDER_BODY_COMPOSITE_ALPHA so circles
    // beneath the body remain partly readable.
    const bodyWidth = chart.circleRadius * field.scale * 2 * SLIDER_BODY_WIDTH_MUL;
    const drawLength = Math.min(slider.length, path.length);

    ctx.save();
    ctx.globalAlpha = alpha * SLIDER_BODY_COMPOSITE_ALPHA;
    this.bodyRenderer.draw(ctx, path, drawLength, field, colour, bodyWidth, dpr);
    ctx.restore();

    // === Tick markers ===
    // Evenly-spaced diamonds along the body (a built-in ruler for the
    // slider's on-screen length). Drawn over the body but below the tail
    // cap, head, and ball.
    this.drawSliderTicks(ctx, obj, slider, path, drawLength, beat, field, innerR, chart.circleRadius, alpha);

    // === Tail / repeat caps ===
    // A small filled circle at the slider's tail position (or each
    // repeat-edge) gives the curve definite endpoints. osu draws
    // these as subtle circles too — without them long sliders look
    // like ribbons trailing off into nothing.
    ctx.save();
    ctx.globalAlpha = alpha;
    const tailPos = pointAtDistance(path, drawLength);
    const tcx = field.x + tailPos.x * field.scale;
    const tcy = field.y + tailPos.y * field.scale;
    ctx.fillStyle = hexToRgba(colour, 0.45);
    ctx.beginPath();
    ctx.arc(tcx, tcy, innerR * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // === Reverse arrows ===
    this.drawReverseArrows(
      ctx,
      obj,
      slider,
      path,
      drawLength,
      beat,
      field,
      colour,
      innerR,
      alpha,
    );

    // === Head circle ===
    // The head is drawn AFTER the body so it sits on top — players
    // need a clean tap target even on a busy slider. We delegate to
    // the same drawCircle path circles use, so head visuals match
    // exactly: combo number, approach ring, perfect-window halo,
    // hit-window glow.
    if (!state.resolvedIds.has(obj.id)) {
      hitObjectRenderer.drawCircle(
        ctx,
        chart,
        obj,
        t,
        field,
        dist,
        perfectWindowBeats,
        goodWindowBeats,
        kiaiScaleBoost,
        quality,
      );
    }

    // === Slider ball + follow circle (active phase only) ===
    const isActive = beat >= obj.beat && beat <= slider.endBeat;
    if (isActive) {
      this.drawBallAndFollow(ctx, chart, obj, slider, path, beat, field, state, alpha, colour, innerR);
    }
  }

  /**
   * Slider tick markers: small white diamonds spaced evenly along the
   * body at a fixed osu-unit gap (`SLIDER_TICK_SPACING_MUL × circleRadius`),
   * a built-in ruler that gives the player a sense of the slider's
   * on-screen length before and while they trace it (osu!'s slider-tick
   * dots). The slider ball "eats" each diamond as it sweeps past on the
   * current span — a pure function of the ball's arc position, so no
   * per-tick state is kept and the diamonds return for the next slide on
   * a repeat slider.
   *
   * Visibility per diamond:
   *   - Approach (ball at head): all shown, fading in with the body.
   *   - Active: a diamond fades + swells once the ball passes it on the
   *     current span (`SLIDER_TICK_POP_DISTANCE_MUL` osu-unit window).
   *     On a repeat slider the diamonds the previous span ate fade back
   *     in over `SLIDER_TICK_RESPAWN_BEATS` from the bounce (span 0 is
   *     continuous with the approach, so it never re-fades).
   *   - Fade-out (`beat > endBeat`): {@link sliderArcLengthAt} freezes
   *     the ball at the final span's endpoint so every diamond stays
   *     eaten while the whole slider fades with `alpha`.
   */
  private drawSliderTicks(
    ctx: CanvasRenderingContext2D,
    obj: CircleHitObject,
    slider: NonNullable<CircleHitObject['slider']>,
    path: SliderPath,
    drawLength: number,
    beat: number,
    field: PlayfieldRect,
    innerR: number,
    circleRadius: number,
    alpha: number,
  ): void {
    // Markers along the visible body, spaced by a fixed osu-unit gap so
    // density tracks on-screen length (see sliderTickDistances).
    const distances = sliderTickDistances(drawLength, circleRadius * SLIDER_TICK_SPACING_MUL);
    if (distances.length === 0) {
      return;
    }
    // Ball arc position + travel direction on the active span. Even
    // spans trace head→tail (a diamond is eaten once the ball's arc
    // length passes it); odd spans trace tail→head (eaten once the
    // ball's arc length drops below it).
    const arcLen = sliderArcLengthAt({
      headBeat: obj.beat,
      endBeat: slider.endBeat,
      length: slider.length,
      slides: slider.slides,
      beat,
    });
    const totalBeats = slider.endBeat - obj.beat;
    const spanBeats = totalBeats / slider.slides;
    const elapsed = Math.max(0, Math.min(totalBeats, beat - obj.beat));
    const spanIdx = Math.min(slider.slides - 1, Math.floor(elapsed / spanBeats));
    const forward = spanIdx % 2 === 0;
    const popDistance = circleRadius * SLIDER_TICK_POP_DISTANCE_MUL;
    const baseR = Math.max(innerR * SLIDER_TICK_RADIUS_MUL, SLIDER_TICK_MIN_RADIUS_PX);
    // Diamonds eaten on the previous span are re-collected on the next
    // pass — fade them back in from the bounce rather than snapping.
    // Span 0 is continuous with the approach phase, so it stays at 1.
    // The fade window is capped to half the span so it always completes
    // even on very-high-SV repeat sliders whose span is shorter than
    // SLIDER_TICK_RESPAWN_BEATS.
    const respawnBeats = Math.min(SLIDER_TICK_RESPAWN_BEATS, spanBeats * 0.5);
    const respawn =
      spanIdx >= 1 ? Math.min(1, (elapsed - spanIdx * spanBeats) / respawnBeats) : 1;

    ctx.save();
    ctx.fillStyle = '#ffffff';
    for (const dist of distances) {
      const eaten = forward ? arcLen - dist : dist - arcLen;
      // Pop drives both the fade and the swell; respawn only dims the
      // alpha on a span reset, so a re-collected diamond fades in at
      // its normal size rather than shrinking.
      const popVisibility = eaten > 0 ? Math.max(0, 1 - eaten / popDistance) : 1;
      const tickAlpha = alpha * SLIDER_TICK_ALPHA * popVisibility * respawn;
      if (tickAlpha <= 0) {
        continue;
      }
      const pos = pointAtDistance(path, dist);
      const cx = field.x + pos.x * field.scale;
      const cy = field.y + pos.y * field.scale;
      const r = baseR * (1 + (1 - popVisibility) * SLIDER_TICK_POP_SWELL);
      ctx.globalAlpha = tickAlpha;
      ctx.beginPath();
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx + r, cy);
      ctx.lineTo(cx, cy + r);
      ctx.lineTo(cx - r, cy);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  /**
   * Reverse arrows at the upcoming repeat edge. For slides=2, during
   * the head→tail span the player sees a reverse arrow at the tail;
   * after slide 1 the arrow flips to the head. Drawn as a triangle
   * pointing back along the path — i.e. the direction the ball will
   * travel *after* it bounces off the edge — so the player can
   * anticipate the upcoming reversal.
   */
  private drawReverseArrows(
    ctx: CanvasRenderingContext2D,
    obj: CircleHitObject,
    slider: NonNullable<CircleHitObject['slider']>,
    path: SliderPath,
    drawLength: number,
    beat: number,
    field: PlayfieldRect,
    colour: string,
    innerR: number,
    alpha: number,
  ): void {
    const totalSpanBeats = (slider.endBeat - obj.beat) / Math.max(1, slider.slides);
    if (slider.slides <= 1 || totalSpanBeats <= 0) {
      return;
    }
    const elapsedSpans = Math.max(0, (beat - obj.beat) / totalSpanBeats);
    const currentSpan = Math.floor(elapsedSpans);
    // Show the upcoming-edge arrow during the current span (so it's
    // visible as the ball heads toward it). After all spans are
    // done, no arrow.
    if (currentSpan >= slider.slides) {
      return;
    }
    const hasUpcomingEdge = currentSpan + 1 < slider.slides;
    if (!hasUpcomingEdge) {
      return;
    }
    // Even spans (0, 2, …) trace head→tail; the upcoming edge
    // is the tail (path end). Odd spans the upcoming edge is
    // the head (path start).
    const arrowAtTail = currentSpan % 2 === 0;
    const arrowDist = arrowAtTail ? drawLength : 0;
    const arrowPos = pointAtDistance(path, arrowDist);
    // Point the arrow the way the ball travels *after* it bounces:
    // back along the path from the edge. Sample a point just inside
    // the edge and take the vector from the edge toward that sample,
    // which is exactly the post-bounce direction.
    const tangentSampleDist = arrowAtTail
      ? Math.max(0, drawLength - 8)
      : Math.min(drawLength, 8);
    const tangentSample = pointAtDistance(path, tangentSampleDist);
    const dxT = tangentSample.x - arrowPos.x;
    const dyT = tangentSample.y - arrowPos.y;
    const len = Math.hypot(dxT, dyT);
    const angle = len > 0 ? Math.atan2(dyT, dxT) : 0;
    const acx = field.x + arrowPos.x * field.scale;
    const acy = field.y + arrowPos.y * field.scale;
    const arrowSize = innerR * 0.9;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(acx, acy);
    ctx.rotate(angle);
    ctx.fillStyle = '#ffffff';
    if (!this.reducedMotion && this.quality.chromeShadows) {
      ctx.shadowColor = colour;
      ctx.shadowBlur = 10;
    }
    ctx.beginPath();
    ctx.moveTo(arrowSize, 0);
    ctx.lineTo(-arrowSize * 0.4, arrowSize * 0.55);
    ctx.lineTo(-arrowSize * 0.4, -arrowSize * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /**
   * Slider ball + follow circle for the active phase. Follow circle
   * alpha tracks the engine's tracking state (bright when tracking,
   * dim when lost) so the player gets "you're locked on" feedback.
   * Ball stays full brightness regardless so its position always
   * reads even when tracking has dropped.
   */
  private drawBallAndFollow(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    obj: CircleHitObject,
    slider: NonNullable<CircleHitObject['slider']>,
    path: SliderPath,
    beat: number,
    field: PlayfieldRect,
    state: CirclesEngineState,
    alpha: number,
    colour: string,
    innerR: number,
  ): void {
    const arcLen = sliderArcLengthAt({
      headBeat: obj.beat,
      endBeat: slider.endBeat,
      length: slider.length,
      slides: slider.slides,
      beat,
    });
    const ballPos = pointAtDistance(path, arcLen);
    const bcx = field.x + ballPos.x * field.scale;
    const bcy = field.y + ballPos.y * field.scale;
    const followR = chart.circleRadius * field.scale * SLIDER_FOLLOW_RADIUS_MULTIPLIER;

    const progress = state.sliderProgress.get(obj.id);
    const tracking = progress?.trackingNow === true;
    const followAlpha = tracking ? 0.85 : 0.35;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = hexToRgba('#ffffff', followAlpha);
    ctx.lineWidth = Math.max(2, innerR * 0.12);
    ctx.beginPath();
    ctx.arc(bcx, bcy, followR, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    if (!this.reducedMotion && this.quality.chromeShadows) {
      ctx.shadowColor = colour;
      ctx.shadowBlur = 16;
    }
    ctx.beginPath();
    ctx.arc(bcx, bcy, innerR * SLIDER_BALL_RADIUS_MUL, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }
}
